import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import HeaderSearch from "@/components/layout/HeaderSearch";
import { SiteLogo } from "@/components/SiteLogo";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface NavItem {
  label: string;
  to: string;
  hash?: string;
}

const navItems: NavItem[] = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "About", to: "/", hash: "#about" },
  { label: "Voices", to: "/", hash: "#testimonials" },
  { label: "Contact", to: "/", hash: "#contact" },
];

export const SiteHeader = () => {
  const { pathname } = useLocation();
  const isProducts = pathname.startsWith("/products");

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    if (pathname !== "/") {
      return;
    }
    e.preventDefault();
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  };

  const navLinkClass = (active: boolean) =>
    `link-line font-mono-acc font-bold uppercase tracking-[0.2em] text-sm ${
      active ? "text-vg-ink" : "text-vg-gray"
    }`;

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5"
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 md:py-5 flex items-center gap-3 sm:gap-4 lg:gap-8">
        <Link to="/" className="flex shrink-0 items-center" data-testid="nav-logo">
          <SiteLogo className="h-10 sm:h-12 md:h-14 w-auto" />
        </Link>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-3 sm:gap-4 lg:gap-8">
          <nav className="hidden lg:flex items-center gap-10 shrink-0">
            {navItems.map((item) => {
              const href = item.hash ? `${item.to}${item.hash}` : item.to;
              const active =
                item.to === "/products"
                  ? isProducts
                  : item.to === "/" && !item.hash
                    ? pathname === "/" && !isProducts
                    : false;

              if (item.hash) {
                return (
                  <Link
                    key={item.label}
                    to={href}
                    onClick={(e) => handleHashClick(e, item.hash!)}
                    className="link-line font-mono-acc font-bold uppercase tracking-[0.2em] text-sm text-vg-ink"
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={navLinkClass(active)}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <HeaderSearch className="hidden md:block w-full max-w-[220px] lg:max-w-[280px] xl:max-w-xs" />

          <Link
            to="/products"
            className="btn-invert hidden sm:inline-flex shrink-0 bg-vg-navy text-white px-4 sm:px-5 py-2 sm:py-2.5 font-mono-acc uppercase tracking-[0.2em] text-xs hover:bg-vg-cyan hover:text-white"
            data-testid="nav-cta-button"
          >
            Shop Now
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                className="lg:hidden inline-flex items-center justify-center h-10 w-10 border border-black/10 text-vg-ink hover:bg-black/5 transition-colors"
                aria-label="Open menu"
                data-testid="nav-mobile-menu-button"
              >
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-sm bg-white border-black/10 p-0">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="px-6 py-5 border-b border-black/10">
                  <SiteLogo className="h-10 w-auto" />
                </div>
                <nav className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-1">
                  {navItems.map((item) => {
                    const href = item.hash ? `${item.to}${item.hash}` : item.to;
                    const active =
                      item.to === "/products"
                        ? isProducts
                        : item.to === "/" && !item.hash
                          ? pathname === "/" && !isProducts
                          : false;

                    if (item.hash) {
                      return (
                        <SheetClose asChild key={item.label}>
                          <Link
                            to={href}
                            onClick={(e) => handleHashClick(e, item.hash!)}
                            className="block px-4 py-3 border border-black/10 font-mono-acc font-bold uppercase tracking-[0.2em] text-sm text-vg-ink hover:bg-[#f9fafb] transition-colors"
                            data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                          >
                            {item.label}
                          </Link>
                        </SheetClose>
                      );
                    }

                    return (
                      <SheetClose asChild key={item.label}>
                        <Link
                          to={item.to}
                          className={`block px-4 py-3 border font-mono-acc font-bold uppercase tracking-[0.2em] text-sm transition-colors ${
                            active
                              ? "bg-vg-navy text-white border-vg-navy"
                              : "border-black/10 text-vg-gray hover:bg-[#f9fafb] hover:text-vg-ink"
                          }`}
                          data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    );
                  })}
                </nav>
                <div className="px-6 py-6 border-t border-black/10 space-y-4 mt-auto">
                  <HeaderSearch className="w-full md:hidden" />
                  <SheetClose asChild>
                    <Link
                      to="/products"
                      className="btn-invert flex w-full items-center justify-center bg-vg-navy text-white px-5 py-3 font-mono-acc uppercase tracking-[0.2em] text-xs hover:bg-vg-cyan hover:text-white sm:hidden"
                      data-testid="nav-mobile-cta-button"
                    >
                      Shop Now
                    </Link>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
};

export default SiteHeader;
